import { useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { CheckCircle2, AlertTriangle, Clock, CircleDashed } from "lucide-react";
import {
  DashFrame, KpiCard, FilterSelect, DataTable, ChartCaption, ChartTitle, LegendDots, StatusPill,
  tooltipDollar, usePalette, GRID, AXIS, TOOLTIP_STYLE, CURSOR,
} from "./kit";
import { dollar } from "../lib/format";

const STATUS = {
  Recovered: { key: "teal", icon: CheckCircle2 },
  Flagged: { key: "red", icon: AlertTriangle },
  "Under review": { key: "orange", icon: Clock },
  Cleared: { key: "slate", icon: CircleDashed },
};

const initial = { supplier: "All suppliers", discrepancy: "All types", status: "All statuses" };

export default function InvoiceReconDash({ rows }) {
  const [f, setF] = useState(initial);
  const c = usePalette();
  const suppliers = [...new Set(rows.map((r) => r.supplier))];
  const types = [...new Set(rows.map((r) => r.discrepancy))];
  const statuses = [...new Set(rows.map((r) => r.status))];

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (f.supplier === "All suppliers" || r.supplier === f.supplier) &&
          (f.discrepancy === "All types" || r.discrepancy === f.discrepancy) &&
          (f.status === "All statuses" || r.status === f.status)
      ),
    [rows, f]
  );

  const kpis = useMemo(() => {
    const invoiced = filtered.reduce((s, r) => s + r.invoiced, 0);
    const perContract = filtered.reduce((s, r) => s + r.per_contract, 0);
    const variance = invoiced - perContract;
    const outstanding = filtered
      .filter((r) => r.status === "Flagged" || r.status === "Under review")
      .reduce((s, r) => s + (r.invoiced - r.per_contract), 0);
    const recovered = filtered
      .filter((r) => r.status === "Recovered")
      .reduce((s, r) => s + (r.invoiced - r.per_contract), 0);
    const exceptions = filtered.filter((r) => r.status !== "Cleared").length;
    return { invoiced, perContract, variance, outstanding, recovered, exceptions };
  }, [filtered]);

  const bySupplier = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      m[r.supplier] = m[r.supplier] || { supplier: r.supplier.replace(/ (Supplies|Logistics|Packaging|Timber|Ceramics|Glass|Freight Co)$/, ""), invoiced: 0, contract: 0 };
      m[r.supplier].invoiced += r.invoiced;
      m[r.supplier].contract += r.per_contract;
    });
    return Object.values(m).map((x) => ({ ...x, variance: Math.max(x.invoiced - x.contract, 0) }));
  }, [filtered]);

  const exceptions = filtered.filter((r) => r.status !== "Cleared");

  return (
    <DashFrame
      title="Supplier invoice reconciliation"
      testid="dash-invoice-recon"
      filters={
        <>
          <FilterSelect label="Supplier" testid="filter-supplier" value={f.supplier}
            options={["All suppliers", ...suppliers]} onChange={(v) => setF({ ...f, supplier: v })} />
          <FilterSelect label="Discrepancy type" testid="filter-discrepancy" value={f.discrepancy}
            options={["All types", ...types]} onChange={(v) => setF({ ...f, discrepancy: v })} />
          <FilterSelect label="Resolution status" testid="filter-status" value={f.status}
            options={["All statuses", ...statuses]} onChange={(v) => setF({ ...f, status: v })} />
        </>
      }
      onReset={() => setF(initial)}
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiCard label="Invoiced (sample)" value={dollar(kpis.invoiced)} accent={c.navy} />
        <KpiCard label="Per contract" value={dollar(kpis.perContract)} accent={c.blue} />
        <KpiCard label="Total variance" value={dollar(kpis.variance)} sub="Invoiced above contract terms" accent={c.orange} />
        <KpiCard label="Outstanding" value={dollar(kpis.outstanding)} sub="Flagged + under review" accent={c.red} />
        <KpiCard label="Recovered (sample)" value={dollar(kpis.recovered)} sub={`${kpis.exceptions} exception invoices`} accent={c.teal} />
      </div>

      <div className="mt-8">
        <ChartTitle color={c.navy}>Invoiced vs contract value by supplier (sample, USD)</ChartTitle>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bySupplier} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="supplier" tick={AXIS} />
              <YAxis tick={AXIS} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
              <Tooltip formatter={tooltipDollar} cursor={CURSOR} contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="invoiced" name="Invoiced" fill={c.navy} radius={[3, 3, 0, 0]} />
              <Bar dataKey="contract" name="Per contract" fill={c.blue} radius={[3, 3, 0, 0]} />
              <Bar dataKey="variance" name="Discrepancy" fill={c.orange} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <ChartCaption>Exception invoices — invoices above the contracted terms, with their current status.</ChartCaption>
        <LegendDots items={[
          { label: "Recovered", color: c.teal }, { label: "Under review", color: c.orange },
          { label: "Flagged / unresolved", color: c.red }, { label: "Cleared", color: c.slate },
        ]} />
      </div>

      <div className="mt-6">
        <DataTable
          testid="dash-exceptions-table"
          columns={[
            { key: "id", label: "Invoice" },
            { key: "supplier", label: "Supplier" },
            { key: "month", label: "Month" },
            { key: "invoiced", label: "Invoiced", right: true, render: (r) => dollar(r.invoiced, 2) },
            { key: "per_contract", label: "Per contract", right: true, render: (r) => dollar(r.per_contract, 2) },
            { key: "variance", label: "Variance", right: true, render: (r) => {
                const v = r.invoiced - r.per_contract;
                const unresolved = r.status === "Flagged" || r.status === "Under review";
                return <span className="font-semibold" style={{ color: unresolved ? c.red : c.orange }}>{v > 0 ? "+" : ""}{dollar(v, 2)}</span>;
              } },
            { key: "discrepancy", label: "Type" },
            { key: "status", label: "Status", render: (r) => {
                const st = STATUS[r.status] || STATUS.Cleared;
                return <StatusPill color={c[st.key]} icon={st.icon} testid={`status-pill-${r.id}`}>{r.status}</StatusPill>;
              } },
          ]}
          rows={exceptions}
        />
      </div>
    </DashFrame>
  );
}
